import { BaseApi } from '../apiBase';
import { StructuredApiError } from '../apiErrors';
import { transformKeysToSnake } from '@portal/utils/caseTransforms';

// --- Interfaces ---

// Payload for checking email
interface CheckEmailPayload {
  email: string;
  subdomain: string;
  communityCode?: string;
}

// Response from check_email endpoint
interface CheckEmailResponse {
  status: 'requires_password' | 'accept_invitation' | 'requires_community_code' | 'not_found' | 'error';
  message: string;
  errors?: Record<string, string[]>; // For validation errors
  invitation_resent?: boolean;
  // data?: { email: string; name?: string }; // Optional user data hint
}

interface ValidatePasswordRequest {
  account: {
    email: string;
    password: string;
    subdomain: string;
    invitation_token?: string;
  };
}

// Assuming a simple success/error response for validation
interface ValidatePasswordResponse {
  status: 'success' | 'error';
  message: string;
}

// Payload for sending magic link
interface SendMagicLinkPayload {
  email: string;
  subdomain: string;
  redirect_url?: string | null; // Optional redirect URL
}

// Response from send_magic_link endpoint
interface SendMagicLinkResponse {
  status: 'success' | 'error';
  message: string;
}

// Payload for sending forgot password email
interface SendForgotPasswordPayload {
  email: string;
  subdomain: string;
}

// Response from send_forgot_password endpoint
interface SendForgotPasswordResponse {
  status: 'success' | 'error';
  message: string;
}

// Payload for resetting password
interface ResetPasswordPayload {
  account: {
    password: string;
    password_confirmation: string;
    reset_password_token: string;
  };
}

// Response from reset_password endpoint
interface ResetPasswordResponse {
  status: 'success' | 'error';
  message: string;
  errors?: Record<string, string[]>; // For validation errors
  data?: {
    email?: string; // Include email on success if API provides it
  };
}

// Payload for sign in
interface SignInPayload {
  account: {
    email: string;
    password: string;
    subdomain: string;
  };
}

// Response from sign in
interface SignInResponse {
  status: 'success' | 'error' | 'unconfirmed';
  message: string;
  data?: {
    authToken?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    confirmation_required?: boolean;
    can_resend?: boolean;
    created_at?: string;
  };
}


export class AuthApi extends BaseApi {
  constructor() {
    super('/api/portal'); // Base path for portal API
  }

  /**
   * Checks the status of an email address for sign-in.
   * @param payload - Email, subdomain, and optional community code.
   * @returns The status and message indicating the next step.
   * @throws {StructuredApiError} If the request fails or returns validation errors.
   */
  async checkEmail(payload: CheckEmailPayload): Promise<CheckEmailResponse> {
    try {
      // Devise expects parameters nested under the resource name (e.g., 'account')
      // Use postSimpleJson since this endpoint returns a simple JSON response, not JSON:API
      const data = await this.postSimpleJson<CheckEmailResponse>('/sessions/check_email', { account: payload });
      return data;

    } catch (error) {
      // Log or handle specific errors if needed
      if (error instanceof StructuredApiError) {
        console.error('Email check failed:', error.errors);
      } else {
        console.error('An unexpected error occurred during email check:', error);
      }
      // Re-throw for the UI component
      throw error;
    }
  }

  // Method to validate password via API
  async validatePassword(requestData: ValidatePasswordRequest): Promise<ValidatePasswordResponse> {
    try {
      // This endpoint is expected to return 200 OK on valid password,
      // and 401 Unauthorized (or similar) on invalid password, which apiBase handles by throwing StructuredApiError.
      // Use postSimpleJson since this endpoint returns simple JSON
      await this.postSimpleJson<ValidatePasswordResponse>('/sessions/validate_password', requestData);

      // If the above line doesn't throw, the password is valid
      return { status: 'success', message: 'Password is valid.' };
    } catch (error) {
      if (error instanceof StructuredApiError && error.status === 401) {
        // Specifically handle the 'Invalid password' case
        return { status: 'error', message: error.getBaseError() || 'Invalid password.' };
      } else if (error instanceof StructuredApiError) {
         // Handle other API errors (e.g., 404 Not Found, 500 Server Error)
         return { status: 'error', message: error.getBaseError() || `Validation failed: ${error.status}` };
      }
      // Handle unexpected network errors etc.
      console.error('Unexpected error during password validation:', error);
      return { status: 'error', message: 'An unexpected error occurred during password validation.' };
    }
  }

  /**
   * Requests a magic link email to be sent.
   * @param payload - Email, subdomain, and optional redirect URL.
   * @returns Success or error status and message.
   * @throws {StructuredApiError} If the request fails.
   */
  async sendMagicLink(payload: SendMagicLinkPayload): Promise<SendMagicLinkResponse> {
    try {
      const data = await this.postSimpleJson<SendMagicLinkResponse>('/sessions/send_magic_link', { account: payload });
      return data; // Should contain { status: 'success', message: '...' }

    } catch (error) {
      console.error('Send magic link failed:', error);
      // Re-throw for the UI component
      throw error;
    }
  }

  /**
   * Requests a password reset email to be sent.
   * @param payload - Email and subdomain.
   * @returns Success or error status and message.
   * @throws {StructuredApiError} If the request fails.
   */
  async sendForgotPassword(payload: SendForgotPasswordPayload): Promise<SendForgotPasswordResponse> {
    try {
      // Note: Devise's passwords#create expects params nested under resource name
      const data = await this.postSimpleJson<SendForgotPasswordResponse>('/passwords', { account: payload });
      return data; // Should contain { status: 'success', message: '...' }

    } catch (error) {
      console.error('Send forgot password failed:', error);
      // Re-throw for the UI component
      throw error;
    }
  }

  /**
   * Resets the user's password using a token.
   * @param token - Reset password token from email link.
   * @param password - New password.
   * @param passwordConfirmation - Password confirmation.
   * @returns Success or error status, message, and potentially errors or user email.
   * @throws {StructuredApiError} If the request fails unexpectedly.
   */
  async resetPassword(token: string, password: string, passwordConfirmation: string): Promise<ResetPasswordResponse & { redirectUrl?: string }> {
    try {
      // Devise's passwords#update expects params nested under resource name and uses PUT
      const payload: ResetPasswordPayload = {
        account: {
          password: password,
          password_confirmation: passwordConfirmation,
          reset_password_token: token
        }
      };
      
      const data = await this.putSimpleJson<ResetPasswordResponse & { redirectUrl?: string }>('/passwords', payload);
      return data; // Contains status, message, potentially data.email on success

    } catch (error) {
      // Log specific StructuredApiError details or generic errors
      if (error instanceof StructuredApiError) {
        console.error('Password reset failed:', error.errors || error.message);
      } else {
        console.error('An unexpected error occurred during password reset:', error);
      }
      // Re-throw for the UI component to handle
      throw error;
    }
  }

  /**
   * Signs in a user with email and password.
   * @param payload - Email, password, and subdomain nested under 'account'.
   * @returns Success or error status, message, and potentially user data.
   * @throws {StructuredApiError} If the request fails.
   */
  async resendConfirmation(email: string, subdomain: string): Promise<{ status: string; message: string }> {
    try {
      const response = await this.postSimpleJson('/sessions/resend_confirmation', {
        account: { email, subdomain }
      });
      return response;
    } catch (error) {
      console.error('Error resending confirmation:', error);
      return {
        status: 'error',
        message: 'Failed to resend confirmation email. Please try again.'
      };
    }
  }

  async signIn(payload: SignInPayload): Promise<SignInResponse> {
    try {
      // Make a direct fetch request to avoid automatic redirect handling in BaseApi
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
      const url = `${this.baseUrl}/sessions`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(transformKeysToSnake(payload)),
        credentials: 'same-origin',
        redirect: 'manual', // Prevent automatic redirect following
      });

      // Check if the response was a redirect (3xx status)
      if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
        // Authentication was successful but server tried to redirect
        // We'll consider this a success and let the frontend handle navigation
        return {
          status: 'success',
          message: 'Logged in successfully.',
          data: {
            // We may not have user data from a redirect response,
            // but the session should be established
          }
        };
      }

      // For non-redirect responses, parse the JSON
      if (response.ok) {
        // Check for JWT token in response headers (devise-jwt pattern)
        const authHeader = response.headers.get('Authorization');
        if (authHeader && authHeader.startsWith('Bearer ')) {
          const token = authHeader.substring(7);
          localStorage.setItem('jwtToken', token);
        }

        const jsonData = await response.json();

        // Check if we have the expected response structure
        if (jsonData.status && jsonData.status.code === 200) {
          return {
            status: 'success',
            message: jsonData.status.message || 'Logged in successfully.',
            data: jsonData.data
          };
        }

        // Fallback for unexpected successful response format
        return {
          status: 'success',
          message: 'Logged in successfully.',
          data: jsonData
        };
      } else {
        // Handle error responses
        let errorMessage = 'Invalid email or password';
        let responseStatus: 'error' | 'unconfirmed' = 'error';
        let responseData: any = undefined;
        
        try {
          const errorData = await response.json();
          
          // Check if this is an unconfirmed account response
          if (errorData.status === 'unconfirmed') {
            responseStatus = 'unconfirmed';
            errorMessage = errorData.message || 'Your account needs to be confirmed. Please check your email for a confirmation link.';
            responseData = errorData.data;
          } else if (errorData.status && errorData.status.message) {
            errorMessage = errorData.status.message;
            // Check if the status object indicates unconfirmed
            if (errorData.status.type === 'unconfirmed') {
              responseStatus = 'unconfirmed';
              responseData = errorData.data;
            }
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.errors) {
            // Extract first error message
            const firstError = Object.values(errorData.errors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMessage = firstError[0];
            }
          }
        } catch (e) {
          // If we can't parse error response, use default message
        }

        return {
          status: responseStatus,
          message: errorMessage,
          data: responseData
        };
      }
    } catch (error) {
      console.error('An unexpected error occurred during sign in:', error);
      return {
        status: 'error',
        message: 'An unexpected error occurred. Please try again.'
      };
    }
  }
}

// Export an instance for easy use
export const authApi = new AuthApi();
